import React, { FunctionComponent, ReactElement, useState, memo, useRef, useEffect } from 'react'
import Grid from '@sport1/react-elements/Grid'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Touchable from '@sport1/react-elements/Touchable'
import Input from '@sport1/react-elements/Input'
import withTheme from '@sport1/react-elements/withTheme'
import isEmpty from 'lodash/isEmpty'
import ButtonFont from '../../../Theme/Fonts/Button'
import ThemeInterface from '../../../themes/ThemeInterface'
import lightTheme from '../../../themes/LightTheme'
import SmallBodyFont from '../../../Theme/Fonts/SmallBody'
import { TestID } from '../../../utils/PropTypesDefinitions'
import { logComponentDeprecation } from '../../../utils/Deprecation'

type SearchBarProps = {
    placeholder: string
    onChange?: (inputText: string) => void
    onPress?: () => void
    buttonText: string
    isDark?: boolean
    autoFocus?: boolean
    onCancel?: () => void
    theme?: ThemeInterface
    pinCancelButton?: boolean
} & TestID

const SearchBar: FunctionComponent<SearchBarProps> = ({
    placeholder,
    onChange,
    onPress,
    buttonText,
    isDark,
    autoFocus,
    onCancel,
    theme = lightTheme,
    pinCancelButton,
    testID,
}): ReactElement => {
    const [inputText, setInput] = useState('')
    const [showCancelButton, setShowCancelButton] = useState(autoFocus)
    const inputRef = useRef<Input>(null)
    const searchBarBackgroundColor = isDark ? 'WHITE' : 'moon-3'

    /* istanbul ignore next */
    useEffect(() => {
        logComponentDeprecation('SearchBar')
    }, [])

    return (
        <Grid noExpand horizontal testID={testID}>
            <Grid
                horizontal
                backgroundColor={searchBarBackgroundColor}
                centerVertical
                borderRadius={18}
                height={36}
                paddingRight={5}
                minWidth={0}
            >
                <Grid noExpand marginLeft={5} marginRight={3}>
                    <FontelloIcon name="search" color="keshi-3" fontSize={16} />
                </Grid>
                <Grid minWidth={0}>
                    {onPress ? (
                        <Touchable onPress={onPress}>
                            <Grid noExpand marginTop={1}>
                                <SmallBodyFont color="keshi-3" numberOfLines={1}>
                                    {placeholder}
                                </SmallBodyFont>
                            </Grid>
                        </Touchable>
                    ) : (
                        <Input
                            ref={inputRef}
                            autoFocus={showCancelButton}
                            onChange={(e): void => {
                                if (onChange) {
                                    onChange(e.value)
                                    setInput(e.value)
                                }
                            }}
                            onBlur={(): void => setShowCancelButton(false)}
                            onFocus={(): void => setShowCancelButton(true)}
                            type="text"
                            fontSize={14}
                            paddingX={0}
                            value={inputText}
                            color="BLACK"
                            placeholderTextColor={theme.colors['keshi-3']}
                            backgroundColor={searchBarBackgroundColor}
                            placeholder={placeholder}
                            fontFamily="Sport1-Regular"
                        />
                    )}
                </Grid>
                {!isEmpty(inputText) && onChange ? (
                    <Touchable
                        onPress={(): void => {
                            setInput('')
                            onChange('')
                        }}
                    >
                        <FontelloIcon name="close-x-circle-active" color="keshi-3" fontSize={16} />
                    </Touchable>
                ) : null}
            </Grid>
            {showCancelButton || pinCancelButton ? (
                <Grid noExpand marginLeft={5} centerVertical>
                    <Touchable
                        onPress={(): void => {
                            onCancel && onCancel()
                            if (inputRef && inputRef.current) {
                                inputRef.current.blur()
                            }
                            setShowCancelButton(false)
                        }}
                    >
                        <ButtonFont uppercase={false} color={isDark ? 'pearl' : 'onyx'}>
                            {buttonText}
                        </ButtonFont>
                    </Touchable>
                </Grid>
            ) : null}
        </Grid>
    )
}

export default memo(withTheme(SearchBar)) as FunctionComponent<SearchBarProps>
